'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Plus, 
  Search, 
  GitPullRequest,
  Edit,
  Trash2,
  Users,
  MoreVertical
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/lib/toast';
import { getWorkflowRoles, deleteWorkflowRole, type WorkflowRole } from '@/services/api/organization';
import { ApiClientError } from '@/lib/api-client';

export default function WorkflowRolesPage() {
  const router = useRouter();
  const { t } = useTranslation();
  
  const RULE_TYPE_LABELS: Record<string, { label: string; color: string }> = {
    ORGANIZATION_RELATION: { label: t.organization.roles.ruleTypes.orgRelation, color: 'bg-blue-100 text-blue-800' },
    SYSTEM_ROLE_MAPPING: { label: t.organization.roles.ruleTypes.roleMapping, color: 'bg-green-100 text-green-800' },
    FIXED_USERS: { label: t.organization.roles.ruleTypes.fixedUsers, color: 'bg-purple-100 text-purple-800' },
    DYNAMIC_SCRIPT: { label: t.organization.roles.ruleTypes.dynamicScript, color: 'bg-orange-100 text-orange-800' },
  };
  
  const [roles, setRoles] = useState<WorkflowRole[]>([]);
  const [filteredRoles, setFilteredRoles] = useState<WorkflowRole[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<WorkflowRole | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadRoles();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredRoles(roles);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredRoles(
        roles.filter(
          (role) =>
            role.name.toLowerCase().includes(query) ||
            role.code.toLowerCase().includes(query) ||
            role.description?.toLowerCase().includes(query)
        )
      );
    }
  }, [searchQuery, roles]);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const data = await getWorkflowRoles();
      setRoles(data);
      setFilteredRoles(data);
    } catch (error) {
      console.error('Failed to load workflow roles:', error);
      if (error instanceof ApiClientError) {
        toast.error(`${t.organization.roles.loadFailed}: ${error.message}`);
      } else {
        toast.error(t.organization.roles.loadFailed);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = () => {
    router.push('/organization/roles/workflow-roles/new');
  };

  const handleEditRole = (roleId: string) => {
    router.push(`/organization/roles/workflow-roles/${roleId}`);
  };

  const handleDeleteClick = (role: WorkflowRole) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!roleToDelete) return;

    try {
      setDeleting(true);
      await deleteWorkflowRole(roleToDelete.id);
      toast.success(`${t.organization.roles.deleteSuccess}: "${roleToDelete.name}"`);
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
      loadRoles();
    } catch (error) {
      console.error('Failed to delete workflow role:', error);
      if (error instanceof ApiClientError) {
        toast.error(`${t.common.deleteFailed}: ${error.message}`);
      } else {
        toast.error(t.organization.roles.deleteFailed);
      }
    } finally {
      setDeleting(false);
    }
  };

  const getRuleTypeDisplay = (ruleType: string) => {
    const config = RULE_TYPE_LABELS[ruleType];
    if (!config) return { label: ruleType, color: 'bg-gray-100 text-gray-800' };
    return config;
  };

  const getRuleTypeLabel = (ruleType: string) => {
    const labels: Record<string, string> = {
      ORGANIZATION_RELATION: t.organization.roles.ruleTypes.orgRelation,
      SYSTEM_ROLE_MAPPING: t.organization.roles.ruleTypes.roleMapping,
      FIXED_USERS: t.organization.roles.ruleTypes.fixedUsers,
      DYNAMIC_SCRIPT: t.organization.roles.ruleTypes.dynamicScript,
    };
    return labels[ruleType] || ruleType;
  };

  const getRuleTypeBadgeStyle = (ruleType: string) => {
    const styles: Record<string, { backgroundColor: string; color: string }> = {
      ORGANIZATION_RELATION: { backgroundColor: '#e1eaff', color: '#3370ff' },
      SYSTEM_ROLE_MAPPING: { backgroundColor: '#dff4e8', color: '#00b42a' },
      FIXED_USERS: { backgroundColor: '#f2e8ff', color: '#722ed1' },
      DYNAMIC_SCRIPT: { backgroundColor: '#ffe7d9', color: '#ff7d00' },
    };
    return styles[ruleType] || { backgroundColor: '#f2f3f5', color: '#646a73' };
  };

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#f7f8fa' }}>
      {/* Title bar with toolbar - Feishu style */}
      <div className="bg-white border-b" style={{ borderColor: '#e5e6eb' }}>
        <div className="px-6 h-12">
          <div className="flex items-center justify-between h-full">
            <div className="flex items-center gap-3">
              {/* Search Input */}
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t.organization.roles.searchWorkflowRolesPlaceholder}
                  className="px-3 py-1.5 pl-9 rounded-lg text-sm outline-none transition-all"
                  style={{ 
                    width: '320px',
                    backgroundColor: '#f7f8fa',
                    color: '#1f2329',
                    border: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.backgroundColor = '#eff0f2';
                    e.target.style.boxShadow = '0 0 0 2px rgba(51, 112, 255, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.backgroundColor = '#f7f8fa';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#8f959e' }} />
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Create Workflow Role Button */}
              <button
                onClick={handleCreateRole}
                className="px-4 py-1.5 rounded-lg text-sm font-medium text-white flex items-center transition-all shadow-sm"
                style={{ 
                  backgroundColor: '#00b42a',
                  boxShadow: '0 1px 2px rgba(0, 180, 42, 0.2)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#23c343';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 180, 42, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#00b42a';
                  e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 180, 42, 0.2)';
                }}
              >
                <Plus className="w-4 h-4 mr-1" />
                {t.organization.roles.createWorkflowRole}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-auto">
        <div className="px-6 pt-4 pb-6">
          {/* Workflow Roles Table */}
          <div className="bg-white rounded-lg" style={{ border: '1px solid #e5e6eb' }}>
            <div className="overflow-x-auto">
              {loading ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 mx-auto" style={{ borderTop: '2px solid #00b42a', borderRight: '2px solid transparent' }}></div>
                <p className="mt-4" style={{ color: '#646a73' }}>{t.common.loading}</p>
              </div>
            ) : filteredRoles.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <GitPullRequest className="w-12 h-12 mb-4" style={{ color: '#c9cdd4' }} />
                <p className="text-base mb-2" style={{ color: '#1f2329' }}>
                  {searchQuery ? t.organization.roles.noMatchingWorkflowRoles : t.organization.roles.noWorkflowRoles}
                </p>
                <p className="text-sm mb-6" style={{ color: '#8f959e' }}>
                  {searchQuery ? t.common.tryOtherKeywords : t.organization.roles.noWorkflowRolesHint}
                </p>
                {!searchQuery && (
                  <button
                    onClick={handleCreateRole}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-all shadow-sm"
                    style={{ 
                      backgroundColor: '#00b42a',
                      boxShadow: '0 2px 4px rgba(0, 180, 42, 0.2)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#23c343';
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 180, 42, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#00b42a';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 180, 42, 0.2)';
                    }}
                  >
                    {t.organization.roles.createFirstWorkflowRole}
                  </button>
                )}
              </div>
            ) : (
              <table className="min-w-full">
                <thead style={{ backgroundColor: '#f7f8fa', borderBottom: '1px solid #e5e6eb' }}>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium" style={{ color: '#646a73' }}>
                      {t.organization.roleName}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium" style={{ color: '#646a73' }}>
                      {t.organization.roleCode}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium" style={{ color: '#646a73' }}>
                      {t.organization.roles.ruleType}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium" style={{ color: '#646a73' }}>
                      {t.organization.description}
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium" style={{ color: '#646a73' }}>
                      {t.organization.roles.usedInWorkflows}
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium" style={{ color: '#646a73' }}>
                      {t.common.actions}
                    </th>
                  </tr>
                </thead>
                <tbody style={{ backgroundColor: '#fff' }}>
                  {filteredRoles.map((role, index) => {
                    const ruleTypeDisplay = getRuleTypeDisplay(role.ruleType);
                    
                    return (
                      <tr 
                        key={role.id} 
                        className="transition-colors cursor-pointer"
                        style={{ borderBottom: index < filteredRoles.length - 1 ? '1px solid #f2f3f5' : 'none' }}
                        onClick={() => handleEditRole(role.id)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f7f8fa';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#fff';
                        }}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div 
                              className="w-8 h-8 rounded flex items-center justify-center mr-3"
                              style={{ backgroundColor: '#dff4e8' }}
                            >
                              <GitPullRequest className="w-4 h-4" style={{ color: '#00b42a' }} />
                            </div>
                            <span className="text-sm font-medium" style={{ color: '#1f2329' }}>{role.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <code className="text-xs px-2 py-1 rounded" style={{ backgroundColor: '#f2f3f5', color: '#646a73' }}>
                            {role.code}
                          </code>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium" style={getRuleTypeBadgeStyle(role.ruleType)}>
                            {getRuleTypeLabel(role.ruleType)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm max-w-xs truncate" style={{ color: '#646a73' }}>
                          {role.description || <span style={{ color: '#c9cdd4' }}>-</span>}
                        </td>
                        <td className="px-6 py-4 text-center text-sm" style={{ color: '#1f2329' }}>
                          {role.workflowCount || 0}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center gap-3">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditRole(role.id);
                              }}
                              className="text-sm font-medium transition-colors"
                              style={{ color: '#3370ff' }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.color = '#5087ff';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.color = '#3370ff';
                              }}
                            >
                              {t.common.edit}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(role);
                              }}
                              className="text-sm font-medium transition-colors"
                              style={{ color: '#f54a45' }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.color = '#ff7875';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.color = '#f54a45';
                              }}
                            >
                              {t.common.delete}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            </div>
          </div>

          {/* Statistics */}
          {!loading && filteredRoles.length > 0 && (
            <div className="mt-4 text-sm" style={{ color: '#8f959e' }}>
              {t.organization.roles.totalWorkflowRoles.replace('{count}', filteredRoles.length.toString())}
              {searchQuery && ` (${t.organization.roles.filteredFrom.replace('{count}', roles.length.toString())})`}
            </div>
          )}

          {/* System Notes */}
          {!loading && (
            <div className="mt-6">
              <div className="bg-white rounded-lg p-5" style={{ border: '1px solid #e5e6eb' }}>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#dff4e8' }}>
                    <GitPullRequest className="w-4 h-4" style={{ color: '#00b42a' }} />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium mb-1" style={{ color: '#1f2329' }}>
                      {t.organization.roles.aboutWorkflowRoles}
                    </div>
                    <div className="text-sm" style={{ color: '#646a73' }}>
                      {t.organization.roles.workflowRolesNote}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.organization.roles.confirmDeleteWorkflowRole}</DialogTitle>
            <DialogDescription>
              {t.organization.roles.confirmDeleteWorkflowRoleMessage.replace('{name}', roleToDelete?.name || '')}
              <br />
              {t.organization.roles.cannotUndo}
              {roleToDelete && roleToDelete.workflowCount && roleToDelete.workflowCount > 0 && (
                <div className="mt-2 p-2 rounded text-sm" style={{ backgroundColor: '#fff7e6', border: '1px solid #ffe7ba', color: '#d46b08' }}>
                  ⚠️ {t.organization.roles.roleUsedInWorkflows.replace('{count}', roleToDelete.workflowCount.toString())}
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              {t.common.cancel}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleting}
            >
              {deleting ? t.common.deleting : t.common.confirmDelete}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
